<template>
    <div class="content member-detail-page">
        <div class="container">
            <!-- 돌아가기 버튼 -->
            <button class="btn-back" @click="goBack">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                돌아가기
            </button>

            <!-- 페이지 타이틀 -->
            <div class="page-title">
                <h2 class="tit">라이선스 할당</h2>
            </div>

            <!-- 본문 -->
            <div v-if="state.loading" class="panel">
                <div class="empty">불러오는 중…</div>
            </div>

            <div v-else-if="!state.detail" class="panel">
                <div class="empty">데이터를 찾을 수 없습니다.</div>
            </div>

            <div v-else class="panel">
                <div class="panel-header">
                    <div class="left">
                        <strong>팀원 정보</strong>
                    </div>
                </div>

                <div class="member-info">
                    <div class="info-row">
                        <label>이름</label>
                        <div class="info-value">{{ state.detail[MEMBER_KEYS.name] }}</div>
                    </div>
                    <div class="info-row">
                        <label>이메일</label>
                        <div class="info-value">{{ state.detail[MEMBER_KEYS.email] }}</div>
                    </div>
                    <div class="info-row">
                        <label>역할</label>
                        <div v-if="!canEditMemberRole" class="info-value">
                            {{ state.detail[MEMBER_KEYS.roleName] }}
                        </div>
                        <div v-else class="info-value role-selector">
                            <select v-model="state.editRole" class="role-dropdown">
                                <option v-for="r in roleSelectOptions" :key="r" :value="r">{{ r }}</option>
                            </select>
                            <button class="btn-submit role-change-btn" @click="submitUpdateRole" :disabled="!canSaveRole">역할변경</button>
                        </div>
                    </div>
                    <div class="info-row">
                        <label>계정상태</label>
                        <div class="info-value">{{ state.detail[MEMBER_KEYS.status] || '-' }}</div>
                    </div>
                    <div class="info-row">
                        <label>초대일시</label>
                        <div class="info-value">{{ state.detail[MEMBER_KEYS.invitedAt] || '-' }}</div>
                    </div>
                </div>

                <div class="divider"></div>

                <div class="panel-header">
                    <div class="left">
                        <strong>{{state.detail[MEMBER_KEYS.name]}} 님의 라이선스 권한</strong>
                    </div>
                </div>

                <div v-if="state.licenseLoading" class="empty">
                    라이선스 정보를 불러오는 중...
                </div>
                <div v-else-if="state.licenseList && state.licenseList.length > 0" class="license-management">
                    <!-- 할당된 라이선스 목록 -->
                    <div v-if="assignedLicenses.length > 0" class="license-section">
                        <h4 class="section-title">할당된 라이선스</h4>
                        <div class="license-list">
                            <div v-for="license in assignedLicenses" :key="license.productName" class="license-item assigned">
                                <div class="license-header">
                                    <h5>{{ license.productName }}</h5>
                                </div>
                                <div class="license-actions">
                                    <button 
                                        @click="unassignLicense(license.productName)"
                                        :disabled="license.isGroupAssignment || license.isOfflineAssignment"
                                        class="btn-submit btn-danger"
                                        :title="getUnassignDisabledMessage(license)"
                                    >
                                        할당 해제
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 미할당된 라이선스 목록 -->
                    <div v-if="unassignedLicenses.length > 0" class="license-section">
                        <h4 class="section-title">미할당된 라이선스</h4>
                        <div class="license-list">
                            <div v-for="license in unassignedLicenses" 
                                :key="license.productName" 
                                class="license-item unassigned clickable"
                                @click="openLicenseModal(license)">
                                <div class="license-header">
                                    <h5>{{ license.productName }}</h5>
                                </div>
                                <div class="license-actions">
                                    <span class="remaining-quantity">남은 수량: {{ license.remainingQuantity }}개</span>
                                    <button 
                                        @click.stop="assignLicense(license.productName)"
                                        :disabled="license.remainingQuantity <= 0"
                                        class="btn-submit"
                                    >
                                        할당
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 라이선스가 없는 경우 -->
                    <div v-if="assignedLicenses.length === 0 && unassignedLicenses.length === 0" class="empty">
                        등록된 라이선스가 없습니다.
                    </div>
                </div>
            </div>
        </div>

        <!-- 라이선스 선택 모달 -->
        <div v-if="state.modal.show" class="ss-modal-backdrop" @click.self="closeLicenseModal">
            <div class="ss-modal ss-modal--scroll-body">
                <h3>라이선스 선택 - {{ state.modal.productName }}</h3>
                <div class="ss-modal-scroll-body">
                    <div class="form">
                        <label>라이선스 선택</label>
                        <select v-model="state.modal.selectedLicenseId" class="license-select">
                            <option value="auto">자동 지정</option>
                            <option 
                                v-for="lic in state.modal.licenses" 
                                :key="lic.licenseId" 
                                :value="lic.licenseId"
                                :disabled="lic.remainingQuantity <= 0">
                                S/N: {{ lic.serialNumber }} | 만료일: {{ formatDate(lic.endDate) }} | 남은 수량: {{ lic.remainingQuantity }}개
                            </option>
                        </select>
                    </div>
                </div>
                <div class="actions">
                    <button class="btn-submit" @click="submitAssignLicense">할당</button>
                    <button class="btn-submit" @click="closeLicenseModal">닫기</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CrewMemberDetail',
    props: {
        crewId: [String, Number],
        memberId: [String, Number],
    },
    data() {
        return {
            MEMBER_KEYS: {
                id: 'id',
                name: 'userName',
                email: 'email',
                role: 'role',
                roleName: 'roleName',
                status: 'status',
                invitedAt: 'invitedAt',
            },
            ROLE_OPTIONS: ['팀 관리자', '보조 관리자', '팀원'],
            SUB_ADMIN_ROLE_OPTIONS: ['보조 관리자', '팀원'],
            sessionCrewRole: null,

            state: {
                loading: false,
                detail: null,
                editRole: '',
                licenseLoading: false,
                licenseList: [],
                modal: {
                    show: false,
                    productName: '',
                    licenses: [],
                    selectedLicenseId: 'auto',
                },
            },
        };
    },
    computed: {
        isSubAdminOperator() {
            return this.sessionCrewRole === 'SUB_ADMIN';
        },
        isTargetTeamAdmin() {
            const name = this.state.detail && this.state.detail[this.MEMBER_KEYS.roleName];
            return name === '팀 관리자';
        },
        /** 보조 관리자는 팀 관리자 멤버의 역할을 바꿀 수 없음 */
        canEditMemberRole() {
            if (!this.state.detail) return false;
            if (this.isSubAdminOperator && this.isTargetTeamAdmin) return false;
            return true;
        },
        roleSelectOptions() {
            if (this.isSubAdminOperator) {
                return this.SUB_ADMIN_ROLE_OPTIONS;
            }
            return this.ROLE_OPTIONS;
        },
        canSaveRole() {
            if (!this.state.detail || !this.canEditMemberRole) return false;
            return this.state.editRole && this.state.editRole !== this.state.detail[this.MEMBER_KEYS.roleName];
        },
        assignedLicenses() {
            if (!this.state.licenseList) return [];
            return this.state.licenseList.filter(license => license.isAssignedToMember);
        },
        unassignedLicenses() {
            if (!this.state.licenseList) return [];
            return this.state.licenseList.filter(license => !license.isAssignedToMember);
        },
    },
    mounted() {
        if (typeof this.getSessionProc === 'function') {
            const acc = this.getSessionProc();
            this.sessionCrewRole = acc?.crewRole || null;
        }
        // CSS 파일 동적 로드
        if (!document.querySelector('link[href="/ssflex/assets/css/crew-license-manager.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/ssflex/assets/css/crew-license-manager.css';
            document.head.appendChild(link);
        }
        this.loadLicenseInfo();
    },
    methods: {
        goBack() {
            const crewId = this.crewId ?? this.$route.params.crewId;
            // 사용자 관리의 팀원별 탭으로 돌아가기
            this.$router.push({
                name: 'licenseManagement',
                query: { 
                    crewId: crewId,
                    mainTab: 'user',
                    subTab: 'members'
                }
            });
        },
        submitUpdateRole() {
            if (!this.canEditMemberRole) return;
            const crewId = this.crewId ?? this.$route.params.crewId;
            const memberId = this.memberId ?? this.$route.params.memberId;
            if (!crewId || !memberId || !this.canSaveRole) return;

            // 팀 관리자로 변경하려는 경우 경고 메시지 표시
            if (this.state.editRole === '팀 관리자') {
                this.showCrewAdminWarning(() => {
                    this.performRoleUpdate(crewId, memberId);
                });
            } else {
                this.performRoleUpdate(crewId, memberId);
            }
        },

        showCrewAdminWarning(callback) {
            const message = '이 멤버를 팀 관리자로 변경하면 기존 팀 관리자는 자동으로 보조 관리자로 변경됩니다.\n\n' + '계속하시겠습니까?';
            
            if (this.swalUtils && typeof this.swalUtils.confirm === 'function') {
                this.swalUtils.confirm('역할 변경 확인', message, callback);
            } else if (this.$swal) {
                this.$swal({
                    title: '역할 변경 확인',
                    text: message,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '확인',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#007bff',
                    cancelButtonColor: '#6c757d',
                }).then((result) => {
                    if (result.isConfirmed) {
                        callback();
                    }
                });
            } else {
                // fallback: native confirm
                if (confirm(message)) {
                    callback();
                }
            }
        },

        performRoleUpdate(crewId, memberId) {
            const payload = { crewId, memberId, role: this.state.editRole };
            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/setRole',
                payload,
                (res) => {
                    const data = res?.data || {};
                    if (data.status === 'ok') {
                        this.gritterOk('역할이 변경되었습니다.');
                        if (this.state.detail) {
                            this.state.detail[this.MEMBER_KEYS.roleName] = this.state.editRole;
                        }
                    } else {
                        this.gritterErr(data.msg || '역할 변경 실패');
                    }
                }
            );
        },

        loadLicenseInfo() {
            const crewId = this.crewId ?? this.$route.params.crewId;
            const memberId = this.memberId ?? this.$route.params.memberId;

            if (!crewId || !memberId) {
                this.gritterErr('잘못된 접근입니다.');
                this.$router.replace({ name: 'licenseManagement' });
                return;
            }

            this.state.loading = true;
            this.state.licenseLoading = true;
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getCrewMemberLicenseInfo',
                { crewId, memberId },
                (res) => {
                    try {
                        const data = res?.data || {};
                        if (data.status === 'ok') {
                            // 멤버 정보 설정
                            this.state.detail = data.memberInfo;
                            const roleName = data.memberInfo[this.MEMBER_KEYS.roleName] || '';
                            this.state.detail[this.MEMBER_KEYS.roleName] = roleName;
                            const opts = this.isSubAdminOperator ? this.SUB_ADMIN_ROLE_OPTIONS : this.ROLE_OPTIONS;
                            this.state.editRole = roleName || opts[0] || '';
                            
                            // 라이선스 정보 설정
                            this.state.licenseList = data.licenseList || [];
                        } else {
                            this.gritterErr(data.msg || '정보 조회 실패');
                        }
                    } finally {
                        this.state.loading = false;
                        this.state.licenseLoading = false;
                    }
                }
            );
        },

        openLicenseModal(license) {
            this.state.modal.productName = license.productName;
            this.state.modal.licenses = license.licenses || [];
            this.state.modal.selectedLicenseId = 'auto';
            this.state.modal.show = true;
        },

        closeLicenseModal() {
            this.state.modal.show = false;
            this.state.modal.productName = '';
            this.state.modal.licenses = [];
            this.state.modal.selectedLicenseId = 'auto';
        },

        submitAssignLicense() {
            const selectedId = this.state.modal.selectedLicenseId;
            const productName = this.state.modal.productName;

            if (selectedId === 'auto') {
                // 자동 지정: 기존 assignLicense 사용
                this.assignLicense(productName);
            } else {
                // 특정 라이선스 지정: assignLicenseOne 사용
                this.assignLicenseOne(selectedId);
            }

            this.closeLicenseModal();
        },

        assignLicense(productName) {
            const crewId = this.crewId ?? this.$route.params.crewId;
            const memberId = this.memberId ?? this.$route.params.memberId;

            if (!crewId || !memberId) {
                this.gritterErr('잘못된 접근입니다.');
                return;
            }

            const payload = { crewId, memberId, productName };
            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/assignLicense',
                payload,
                (res) => {
                    const data = res?.data || {};
                    if (data.status === 'ok') {
                        this.gritterOk('라이선스가 할당되었습니다.');
                        this.loadLicenseInfo(); // 라이선스 정보 다시 로드
                    } else {
                        this.gritterErr(data.msg || '라이선스 할당 실패');
                    }
                }
            );
        },

        assignLicenseOne(licenseId) {
            const crewId = this.crewId ?? this.$route.params.crewId;
            const memberId = this.memberId ?? this.$route.params.memberId;

            if (!crewId || !memberId || !licenseId) {
                this.gritterErr('잘못된 접근입니다.');
                return;
            }

            const payload = { crewId, memberId, licenseId };
            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/assignLicenseOne',
                payload,
                (res) => {
                    const data = res?.data || {};
                    if (data.status === 'ok') {
                        this.gritterOk('라이선스가 할당되었습니다.');
                        this.loadLicenseInfo(); // 라이선스 정보 다시 로드
                    } else {
                        this.gritterErr(data.msg || '라이선스 할당 실패');
                    }
                }
            );
        },

        unassignLicense(productName) {
            const crewId = this.crewId ?? this.$route.params.crewId;
            const memberId = this.memberId ?? this.$route.params.memberId;

            if (!crewId || !memberId) {
                this.gritterErr('잘못된 접근입니다.');
                return;
            }

            const payload = { crewId, memberId, productName };
            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/unassignLicense',
                payload,
                (res) => {
                    const data = res?.data || {};
                    if (data.status === 'ok') {
                        this.gritterOk('라이선스 할당이 해제되었습니다.');
                        this.loadLicenseInfo(); // 라이선스 정보 다시 로드
                    } else {
                        this.gritterErr(data.msg || '라이선스 할당 해제 실패');
                    }
                }
            );
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('ko-KR');
        },

        getUnassignDisabledMessage(license) {
            if (license.isGroupAssignment && license.isOfflineAssignment) {
                return '그룹 할당 및 오프라인 할당으로 할당된 라이선스는 개별 해제가 불가능합니다.';
            } else if (license.isGroupAssignment) {
                return '그룹 할당으로 할당된 라이선스는 개별 해제가 불가능합니다.';
            } else if (license.isOfflineAssignment) {
                return '오프라인 할당으로 할당된 라이선스는 개별 해제가 불가능합니다.';
            }
            return '';
        },

        // Toast wrappers (당신 프로젝트 gritter로 통일)
        gritterOk(msg) {
            this.swalUtils?.gritter
                ? this.swalUtils.gritter('Success', msg || '성공', 'success', 2000)
                : console.log('[OK]', msg);
        },
        gritterErr(msg) {
            const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.';
            this.swalUtils?.gritter
                ? this.swalUtils.gritter('error', m, 'error', 2000)
                : console.error('[ERR]', m);
        },
    },
};
</script>

<style scoped>
/* 외부 CSS 파일 사용: /ssflex/assets/css/crew-license-manager.css */
/* 추가 컴포넌트별 스타일 있을 시 /ssflex/assets/css/crew-license-manager.css에 작성*/
</style>